import React, { useEffect, useState } from 'react';

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const fetchContacts = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch('/api/admin/contacts', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error('Failed to fetch contacts');
      const data = await res.json();
      setContacts(data);
    } catch (err) {
      setError('Error loading contacts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    setDeletingId(id);
    try {
      const res = await fetch(`/api/admin/contacts/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error('Failed to delete');
      setContacts(contacts.filter(c => c._id !== id));
    } catch (err) {
      alert('Error deleting contact.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch('/api/admin/contacts/export', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error('Failed to export');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contacts.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error exporting contacts.');
    } finally {
      setExporting(false);
    }
  };

  const openModal = (contact) => {
    setSelected(contact);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelected(null);
  };

  return (
    <div className="p-8 bg-white rounded-lg shadow-lg max-w-5xl mx-auto mt-12">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Contact Submissions</h2>
        <button
          onClick={handleExport}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
          disabled={exporting}
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>
      {loading && <div>Loading...</div>}
      {error && <div className="text-red-600">{error}</div>}
      {!loading && !error && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contacts.map((c) => (
                <tr key={c._id} className="hover:bg-blue-50 transition cursor-pointer">
                  <td className="px-4 py-2 whitespace-nowrap" onClick={() => openModal(c)}>{c.name}</td>
                  <td className="px-4 py-2 whitespace-nowrap" onClick={() => openModal(c)}>{c.email}</td>
                  <td className="px-4 py-2 whitespace-pre-line" onClick={() => openModal(c)}>{c.message.slice(0, 40)}{c.message.length > 40 ? '...' : ''}</td>
                  <td className="px-4 py-2 whitespace-nowrap" onClick={() => openModal(c)}>{new Date(c.createdAt).toLocaleString()}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <button
                      onClick={() => handleDelete(c._id)}
                      className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition mr-2"
                      disabled={deletingId === c._id}
                    >
                      {deletingId === c._id ? 'Deleting...' : 'Delete'}
                    </button>
                    <button
                      onClick={() => openModal(c)}
                      className="bg-gray-200 text-gray-800 px-3 py-1 rounded hover:bg-gray-300 transition"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Modal for viewing details */}
      {showModal && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-lg w-full relative">
            <button
              onClick={closeModal}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              &times;
            </button>
            <h3 className="text-xl font-bold mb-4">Contact Details</h3>
            <div className="mb-2"><span className="font-semibold">Name:</span> {selected.name}</div>
            <div className="mb-2"><span className="font-semibold">Email:</span> {selected.email}</div>
            <div className="mb-2"><span className="font-semibold">Message:</span> <pre className="whitespace-pre-wrap inline">{selected.message}</pre></div>
            <div className="mb-2"><span className="font-semibold">Date:</span> {new Date(selected.createdAt).toLocaleString()}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContacts; 